/**
 * Browser polyfills — must be imported FIRST in the app entry (`main.tsx`), before any
 * other module, so the globals exist before dependent code (lazy chunks included) runs.
 *
 * Why this exists: the Solana payment path pulls in `@reown/appkit-adapter-solana`,
 * `@solana/web3.js`, `@solana/spl-token`, and a transitive crypto/`readable-stream` stack,
 * several of which reference Node globals (`Buffer`, `process`) directly without importing a
 * polyfill. Vite/Rolldown does not polyfill Node globals for the browser, so:
 *   - `Buffer` (e.g. `Buffer.from(instruction.data, 'hex')`) threw `Can't find variable:
 *     Buffer` on every Solana USDC adoption — the happy path (notably in mobile in-app
 *     wallet browsers like Phantom on iOS).
 *   - `process.nextTick` is referenced unguarded in a `readable-stream` writable error path
 *     within the wallet/crypto chunk; it would throw `Can't find variable: process` the same
 *     way if that error path runs.
 * The Sui and EVM paths don't touch either global, which is why only Solana was affected.
 *
 * We expose the userland `buffer` package as `globalThis.Buffer`, and a minimal browserify-
 * style `process` shim (browser=true, empty env, `nextTick` via `queueMicrotask`) as
 * `globalThis.process`. `global` is intentionally NOT polyfilled: every `global.*` reference
 * in the bundle is already guarded by `typeof global` checks, and defining it risks flipping
 * isomorphic libraries into their Node code paths.
 */
import { Buffer } from 'buffer';

if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}

// Minimal `process` shim matching the conventional browserify polyfill these deps expect.
// Typed via a local cast so it compiles whether or not @types/node's `Process` type is in scope.
const globalWithProcess = globalThis as unknown as {
  process?: {
    env: Record<string, string | undefined>;
    nextTick: (callback: (...args: unknown[]) => void, ...args: unknown[]) => void;
    version: string;
    versions: Record<string, string>;
    platform: string;
    browser: boolean;
  };
};

if (typeof globalWithProcess.process === 'undefined') {
  globalWithProcess.process = {
    env: {},
    nextTick: (callback, ...args) => queueMicrotask(() => callback(...args)),
    version: '',
    versions: {},
    platform: 'browser',
    browser: true,
  };
}
